import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  Loader2,
  Menu,
  Pause,
  Play,
  SlidersHorizontal,
  TriangleAlert,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useChatLayout } from '../../components/layout/ChatLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { AnomalyCard } from '../../components/pulse/AnomalyCard';
import { useCountdown } from '../../hooks/useCountdown';
import { pulseApi, type PulseAccount, type PulseAnomalyCard } from '../../services/pulse-api';
import { handleApiError } from '../../services/api';

/**
 * Pulse — active account detail (Screen C). Shows the run-status header block
 * and the recent anomalies list for this account.
 */
const PulseAccountPage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const { isMobile, toggleSidebar } = useChatLayout();
  const queryClient = useQueryClient();

  const accountQuery = useQuery({
    queryKey: ['pulse', 'account', accountId],
    queryFn: () => pulseApi.getAccount(accountId!),
    enabled: !!accountId,
    refetchInterval: 30_000,
  });

  const anomaliesQuery = useInfiniteQuery({
    queryKey: ['pulse', 'anomalies', { accountId }],
    queryFn: ({ pageParam }) =>
      pulseApi.listAccountAnomalies(accountId!, { cursor: pageParam, limit: 20 }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!accountId,
  });

  const invalidateAccount = () => {
    queryClient.invalidateQueries({ queryKey: ['pulse', 'account', accountId] });
    queryClient.invalidateQueries({ queryKey: ['pulse', 'accounts'] });
  };
  const pauseMutation = useMutation({
    mutationFn: () => pulseApi.pause(accountId!),
    onSuccess: () => {
      invalidateAccount();
      toast.success('Monitoring paused');
    },
    onError: (error: unknown) =>
      toast.error(handleApiError(error) || 'Failed to pause monitoring'),
  });
  const resumeMutation = useMutation({
    mutationFn: () => pulseApi.resume(accountId!),
    onSuccess: () => {
      invalidateAccount();
      toast.success('Monitoring resumed');
    },
    onError: (error: unknown) =>
      toast.error(handleApiError(error) || 'Failed to resume monitoring'),
  });

  const account = accountQuery.data;
  const items = anomaliesQuery.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-3xl mx-auto space-y-6">
        <Link
          to="/pulse"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" /> Back to accounts
        </Link>

        {accountQuery.isLoading && (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {accountQuery.error && (
          <div className="rounded-xl bg-destructive/15 border border-destructive/20 text-destructive p-4">
            Failed to load this account.
          </div>
        )}

        {account && (
          <>
            <AccountHeader
              account={account}
              onPause={() => pauseMutation.mutate()}
              onResume={() => resumeMutation.mutate()}
              isMutating={pauseMutation.isPending || resumeMutation.isPending}
            />
            {account.state === 'paused' && <PausedBanner />}
            <StatHeader account={account} />
            <ExecutionHealthStrip account={account} />
            <AnomaliesSection
              loading={anomaliesQuery.isLoading}
              items={items}
              hasMore={!!anomaliesQuery.hasNextPage}
              isFetchingMore={anomaliesQuery.isFetchingNextPage}
              onLoadMore={() => anomaliesQuery.fetchNextPage()}
            />
          </>
        )}
      </div>
    </div>
  );
};

const AccountHeader: React.FC<{
  account: PulseAccount;
  onPause: () => void;
  onResume: () => void;
  isMutating: boolean;
}> = ({ account, onPause, onResume, isMutating }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-muted grid place-items-center">
        <Building2 className="w-5 h-5 text-muted-foreground" />
      </div>
      <div>
        <h1 className="text-xl font-bold text-foreground">{account.accountName}</h1>
        <div className="text-sm text-muted-foreground">{account.dataSourceName}</div>
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <Link
        to={`/pulse/accounts/${account.accountId}/activate`}
        className="inline-flex items-center gap-1.5 text-sm font-medium border border-input dark:border-white/10 rounded-lg px-3 py-2 hover:bg-muted dark:hover:bg-white/10"
      >
        <SlidersHorizontal className="w-4 h-4" /> Manage signals
      </Link>
      {account.state === 'paused' ? (
        <button
          onClick={onResume}
          disabled={isMutating}
          className="inline-flex items-center gap-1.5 text-sm font-medium bg-primary text-white rounded-lg px-3 py-2 hover:bg-primary transition-colors disabled:opacity-50"
        >
          {isMutating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Resume monitoring
        </button>
      ) : (
        <button
          onClick={onPause}
          disabled={isMutating}
          className="inline-flex items-center gap-1.5 text-sm font-medium border border-input dark:border-white/10 rounded-lg px-3 py-2 hover:bg-muted dark:hover:bg-white/10 disabled:opacity-50"
        >
          {isMutating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />}
          Pause monitoring
        </button>
      )}
    </div>
  </div>
);

const PausedBanner: React.FC = () => (
  <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted border border-border rounded-lg px-4 py-3">
    <Pause className="w-4 h-4 shrink-0" />
    Monitoring is paused — Pulse won't run scheduled checks or raise new anomalies for this
    account until you resume.
  </div>
);

const StatHeader: React.FC<{ account: PulseAccount }> = ({ account }) => {
  const exec = account.executionJob;
  const isPaused = account.state === 'paused';
  const countdown = useCountdown(exec?.nextRun);

  return (
    <div className="rounded-xl bg-white dark:bg-white/5 border border-border p-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Last run" value={formatTime(exec?.lastRun)} sub={formatRelativeDay(exec?.lastRun)} />
        <Stat
          label="Next run"
          value={isPaused ? 'Paused' : (countdown ?? '—')}
          sub={isPaused ? 'resume to schedule' : formatTime(exec?.nextRun)}
          highlight={!isPaused}
        />
        <Stat label="Frequency" value={exec?.scheduleLabel ?? '—'} sub="2× sync interval" />
        <Stat
          label="Signals refreshed"
          value={formatTime(account.signalsLastRefreshedAt)}
          sub="daily"
        />
      </div>
    </div>
  );
};

const ExecutionHealthStrip: React.FC<{ account: PulseAccount }> = ({ account }) => {
  const exec = account.executionJob;
  if (!exec?.lastError) return null;
  return (
    <div className="flex items-center gap-2 text-xs text-warn bg-warn/15 border border-warn/20 rounded-lg px-3 py-2">
      <TriangleAlert className="w-3.5 h-3.5 shrink-0" />
      Last execution failed (<span className="font-mono">{exec.lastError}</span>) — retrying on the next run.
    </div>
  );
};

const AnomaliesSection: React.FC<{
  loading: boolean;
  items: PulseAnomalyCard[];
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
}> = ({ loading, items, hasMore, isFetchingMore, onLoadMore }) => (
  <section className="space-y-3">
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Recent anomalies
      </h2>
      <div className="text-xs text-muted-foreground">Most recent first</div>
    </div>

    {loading && (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    )}

    {!loading && items.length === 0 && (
      <div className="rounded-xl bg-muted border border-border p-8 text-center">
        <p className="text-muted-foreground">No anomalies detected yet.</p>
        <p className="text-sm text-muted-foreground mt-1">
          Pulse is watching — anything unusual will show up here.
        </p>
      </div>
    )}

    <div className="space-y-3">
      {items.map((card) => (
        <AnomalyCard key={card.resultId} card={card} />
      ))}
    </div>

    {hasMore && (
      <button
        onClick={onLoadMore}
        disabled={isFetchingMore}
        className="w-full text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg py-2.5 disabled:opacity-50"
      >
        {isFetchingMore ? 'Loading…' : 'Load more'}
      </button>
    )}
  </section>
);

const Stat: React.FC<{ label: string; value: string; sub?: string; highlight?: boolean }> = ({
  label,
  value,
  sub,
  highlight,
}) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div
      className={
        highlight
          ? 'font-semibold mt-0.5 text-primary tabular-nums'
          : 'font-semibold mt-0.5 text-foreground'
      }
    >
      {value}
    </div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
  </div>
);

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatRelativeDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfToday - startOfDate) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default PulseAccountPage;
